// Admin Reports Page — Dashboard with stats
import { renderHeader, renderBottomNav } from '../components/header.js';
import { demoBookings } from '../data/demo-store.js';
import { requireAdmin } from '../auth.js';
import { isFirebaseReady } from '../firebase.js';
import { getAllBookings } from '../services/db.js';
import { t } from '../i18n.js';

let reportStartDate = '';
let reportEndDate = '';

export async function renderAdminReports() {
  const allowed = await requireAdmin();
  if (!allowed) return;

  const app = document.getElementById('app');
  app.innerHTML = `
    ${renderHeader()}
    <div class="page">
      <h2 class="page-title">📊 รายงาน</h2>
      <div style="text-align:center; padding: var(--space-xl);">
        <div class="loading-spinner"></div>
        <p class="text-muted mt-md">กำลังโหลดข้อมูล...</p>
      </div>
    </div>
    ${renderBottomNav('admin-reports')}
  `;

  // Fetch real data
  const allBookingsData = isFirebaseReady() ? await getAllBookings() : demoBookings;

  // Calculate returning customers BEFORE date filtering
  const customerCounts = {};
  allBookingsData.forEach(b => {
    if(b.user_id) {
       customerCounts[b.user_id] = (customerCounts[b.user_id] || 0) + 1;
    }
  });

  // Apply date filter
  let allBookings = allBookingsData;
  if (reportStartDate) {
     allBookings = allBookings.filter(b => b.date >= reportStartDate);
  }
  if (reportEndDate) {
     allBookings = allBookings.filter(b => b.date <= reportEndDate);
  }

  // Calculate new vs returning in filtered chunk
  let newCustomers = 0;
  let returningCustomers = 0;
  const uniqueUsersInFilter = new Set(allBookings.map(b => b.user_id).filter(id => id));
  uniqueUsersInFilter.forEach(userId => {
     if(customerCounts[userId] > 1) {
        returningCustomers++;
     } else {
        newCustomers++;
     }
  });

  const totalBookings = allBookings.length;
  const confirmedBookings = allBookings.filter(b => b.status === 'confirmed').length;
  const completedBookings = allBookings.filter(b => b.status === 'completed').length;
  const pendingBookings = allBookings.filter(b => b.status === 'pending' || b.status === 'paid').length;
  const cancelledBookings = allBookings.filter(b => b.status === 'cancelled').length;

  const totalRevenue = allBookings
    .filter(b => b.status === 'confirmed' || b.status === 'completed')
    .reduce((sum, b) => sum + (b.price || 0), 0);

  const pendingRevenue = allBookings
    .filter(b => b.status === 'pending' || b.status === 'paid')
    .reduce((sum, b) => sum + (b.price || 0), 0);

  // Service type breakdown
  const serviceBreakdown = {};
  allBookings.forEach(b => {
    const type = b.service_type || 'unknown';
    serviceBreakdown[type] = (serviceBreakdown[type] || 0) + 1;
  });

  const SERVICE_LABELS = {
    onsite: '🏠 ' + t('service_onsite'),
    chat: '💬 ' + t('service_chat'),
    call: '📞 ' + t('service_call'),
    video: '📹 ' + t('service_video')
  };

  // Extract Today's Schedule
  const tzOffset = 7 * 60 * 60000;
  const localIsoString = new Date(Date.now() + tzOffset).toISOString().split('T')[0];
  const todayBookings = allBookingsData
    .filter(b => b.date === localIsoString && (b.status === 'confirmed' || b.status === 'completed'))
    .sort((a,b) => {
        if(!a.time) return 1;
        if(!b.time) return -1;
        return a.time.localeCompare(b.time)
    });

  app.innerHTML = `
    ${renderHeader()}
    <div class="page pb-xxl">
      <h2 class="page-title">📊 รายงาน</h2>

      <!-- Today's Schedule -->
      <div class="card mb-lg" style="border: 2px solid var(--accent-gold);">
        <h3 style="color: var(--accent-gold); margin-bottom: var(--space-md);">📌 คิวดูแลวันนี้ (Today's Schedule)</h3>
        ${todayBookings.length > 0 ? `
           <div style="display:flex; flex-direction:column; gap: var(--space-md);">
               ${todayBookings.map(b => `
                  <div style="display:flex; background:var(--bg-glass); border-radius:var(--radius-md); padding:var(--space-sm); border-left: 4px solid ${b.status==='completed'?'#00B900':'#4338CA'}; align-items:center;">
                     <div style="min-width:60px; font-weight:bold; color:var(--text-accent);">${b.time} น.</div>
                     <div style="flex:1;">
                        <div class="fw-600">${b.customer_name}</div>
                        <div class="text-xs text-muted">${SERVICE_LABELS[b.service_type] || b.service_type} - ${b.status === 'completed' ? '✅ ดูเสร็จแล้ว' : '⏳ รอคิวงาน'}</div>
                     </div>
                     <a href="#/booking/${b.id}/payment" class="btn btn-outline" style="padding: 4px 10px; font-size:12px;">ดูข้อมูล</a>
                  </div>
               `).join('')}
           </div>
        ` : `
           <div style="text-align:center; padding:var(--space-md);">
              <div style="font-size:30px; margin-bottom:var(--space-sm);">🛋️</div>
              <p class="text-muted text-sm">ไม่มีคิวจองในวันนี้ พักผ่อนได้เลยครับ!</p>
           </div>
        `}
      </div>

      <!-- Filters -->
      <div class="card mb-lg pb-md">
        <label class="form-label" style="color:var(--accent-gold);">📅 คัดกรองตามวันที่ (Date Range)</label>
        <div style="display:flex; gap:var(--space-sm); flex-wrap:wrap; align-items:center;">
          <input type="date" class="form-input" id="report-start-date" value="${reportStartDate}" style="flex:1; min-width: 120px; font-size:var(--font-sm);" />
          <span style="color:var(--text-muted); font-size:var(--font-sm);">ถึง</span>
          <input type="date" class="form-input" id="report-end-date" value="${reportEndDate}" style="flex:1; min-width: 120px; font-size:var(--font-sm);" />
        </div>
        <div style="display:flex; gap:var(--space-sm); align-items:center; margin-top:var(--space-sm);">
          <button class="btn btn-primary" id="apply-report-filter" style="flex:1; padding: 10px;">🔍 ค้นหา</button>
          <button class="btn btn-outline" id="clear-report-filter" style="flex:1; padding: 10px;">รีเซ็ต</button>
        </div>
      </div>

      <!-- Main Stats -->
      <div class="stat-grid">
        <div class="stat-card">
          <div class="stat-value">${totalBookings}</div>
          <div class="stat-label">การจองทั้งหมด</div>
        </div>
        <div class="stat-card">
          <div class="stat-value">฿${totalRevenue.toLocaleString()}</div>
          <div class="stat-label">รายได้ (ยืนยัน)</div>
        </div>
        <div class="stat-card">
          <div class="stat-value">${confirmedBookings}</div>
          <div class="stat-label">ยืนยันแล้ว</div>
        </div>
        <div class="stat-card">
          <div class="stat-value">${pendingBookings}</div>
          <div class="stat-label">รอดำเนินการ</div>
        </div>
      </div>

      <!-- Revenue card -->
      <div class="card mb-lg" style="background: var(--gradient-card);">
        <h3 style="color: var(--accent-gold); margin-bottom: var(--space-md);">💰 สรุปรายได้</h3>
        <div style="display:grid; gap: var(--space-md);">
          <div class="flex-between">
            <span class="text-muted">รายได้ที่ยืนยัน</span>
            <span class="text-green fw-700">฿${totalRevenue.toLocaleString()}</span>
          </div>
          <div class="flex-between">
            <span class="text-muted">รอตรวจสอบ</span>
            <span class="text-gold fw-600">฿${pendingRevenue.toLocaleString()}</span>
          </div>
          <div class="divider"></div>
          <div class="flex-between">
            <span class="fw-600">Conversion Rate</span>
            <span class="text-purple fw-700">
              ${totalBookings > 0 ? Math.round((confirmedBookings / totalBookings) * 100) : 0}%
            </span>
          </div>
        </div>
      </div>

      <!-- Retention Card -->
      <div class="card mb-lg">
        <h3 style="color: var(--accent-gold); margin-bottom: var(--space-md);">👥 อัตราการดูซ้ำ (Customer Retention)</h3>
        <p class="text-xs text-muted mb-md">วิเคราะห์จากจำนวนคน <b>(${uniqueUsersInFilter.size} คน)</b> ในช่วงเวลาที่เลือก ด้านล่างจะแสดงว่าเขาเหล่านี้จัดเป็นลูกค้าแบบไหน</p>
        <div style="display:grid; grid-template-columns: 1fr 1fr; gap: var(--space-md); text-align:center;">
           <div style="background:var(--bg-glass); padding: var(--space-md); border-radius: var(--radius-md);">
              <div class="text-xl fw-700" style="color:#00B900;">${newCustomers}</div>
              <div class="text-sm text-muted">ลูกค้าใหม่<br /><small>(เพิ่งเคยจอง)</small></div>
           </div>
           <div style="background:var(--bg-glass); padding: var(--space-md); border-radius: var(--radius-md);">
              <div class="text-xl fw-700 text-purple">${returningCustomers}</div>
              <div class="text-sm text-muted">ลูกค้าประจำ<br /><small>(ดูมากกว่า 1 ครั้ง)</small></div>
           </div>
        </div>
      </div>

      <!-- Service Breakdown -->
      <div class="card mb-lg">
        <h3 style="color: var(--accent-gold); margin-bottom: var(--space-md);">📈 แยกตามประเภทบริการ</h3>
        ${Object.keys(serviceBreakdown).length > 0 ? `
          <div style="display:grid; gap: var(--space-md);">
            ${Object.entries(serviceBreakdown).map(([type, count]) => {
    const percentage = totalBookings > 0 ? (count / totalBookings) * 100 : 0;
    return `
                <div>
                  <div class="flex-between mb-sm">
                    <span>${SERVICE_LABELS[type] || type}</span>
                    <span class="fw-600">${count} (${Math.round(percentage)}%)</span>
                  </div>
                  <div style="height:8px; background: var(--bg-glass); border-radius:4px; overflow:hidden;">
                    <div style="height:100%; width:${percentage}%; background: var(--gradient-primary); border-radius:4px; transition: width 0.5s ease;"></div>
                  </div>
                </div>
              `;
  }).join('')}
          </div>
        ` : `
          <p class="text-muted text-sm">ยังไม่มีข้อมูล</p>
        `}
      </div>

      <!-- Status Breakdown -->
      <div class="card">
        <h3 style="color: var(--accent-gold); margin-bottom: var(--space-md);">📋 สถานะการจอง</h3>
        <div style="display:grid; gap: var(--space-sm);">
          <div class="flex-between">
            <span>✅ ดูเสร็จแล้ว</span>
            <span class="badge badge-confirmed" style="background:#00B900;">${completedBookings}</span>
          </div>
          <div class="flex-between">
            <span>📌 รอดำเนินการดูดวง (ยืนยันแล้ว)</span>
            <span class="badge badge-confirmed">${confirmedBookings}</span>
          </div>
          <div class="flex-between">
            <span>⏳ รอตรวจสอบยอดโอน</span>
            <span class="badge badge-pending">${pendingBookings}</span>
          </div>
          <div class="flex-between">
            <span>❌ ยกเลิก</span>
            <span class="badge badge-cancelled">${cancelledBookings}</span>
          </div>
        </div>
      </div>
    </div>
    ${renderBottomNav('admin-reports')}
  `;

  document.getElementById('apply-report-filter')?.addEventListener('click', () => {
     reportStartDate = document.getElementById('report-start-date').value;
     reportEndDate = document.getElementById('report-end-date').value;
     renderAdminReports();
  });
  document.getElementById('clear-report-filter')?.addEventListener('click', () => {
     reportStartDate = '';
     reportEndDate = '';
     renderAdminReports();
  });
}
